import { BillingTier } from '@/__generated__/graphql';
import { env } from '@/config/env';

/**
 * Canonical in-app support surface (A19): ONE support address everywhere —
 * `admin@syncanix.com` per `docs/operations/support-sla.md` § Channels (the
 * same address the marketing footer, DSAR forms, and docs troubleshooting
 * page publish; B25 owns keeping the marketing side consistent). Docs links
 * point at the published marketing-origin docs tree.
 */
const MARKETING_BASE = (env.VITE_MARKETING_URL ?? 'https://www.syncanix.com').replace(/\/+$/, '');

export const SUPPORT_EMAIL = 'admin@syncanix.com';
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;
export const TROUBLESHOOTING_DOCS_URL = `${MARKETING_BASE}/docs/guides/troubleshooting`;

/**
 * The "Contact sales" destination for the dashboard — the marketing site's
 * embedded /contact form, which defaults to the Sales topic (operator directive
 * 2026-07-16). Used by the sales-led billing CTAs; keeps sales enquiries in the
 * one lead-capture form rather than a bare mailto.
 */
export const SALES_CONTACT_URL = `${MARKETING_BASE}/contact`;

/**
 * The `reportOutcome` integration reference (Phase 5.1).
 *
 * Deep-linked to the section rather than the page, because the reader is a
 * customer-success manager forwarding it to an engineer: they cannot evaluate
 * whether the right heading is three screens down, and a link that lands
 * somewhere plausible-but-wrong is worse than none.
 */
export const REPORT_OUTCOME_DOCS_URL = `${MARKETING_BASE}/docs/reference/runtime-api#report-outcome`;

/**
 * The customer-facing first-response promise per tier, restating the
 * support-sla.md "Tiers at a glance" table (P1 FRT) — kept TRUTHFUL against
 * the matrix (the B23 audit class): Starter is a paid email tier with NO
 * published SLA (best-effort, never "community"); Enterprise terms live in
 * the signed contract, so the dashboard never states a hard 24/7 promise.
 * Null tier = the dev entry tier (community-supported).
 */
export function supportSlaKey(tier: BillingTier | null): string {
  switch (tier) {
    case BillingTier.Starter:
      return 'support.sla.starter';
    case BillingTier.Growth:
      return 'support.sla.growth';
    case BillingTier.Scale:
      return 'support.sla.scale';
    case BillingTier.Enterprise:
      return 'support.sla.enterprise';
    case null:
      return 'support.sla.community';
  }
}
